/**
 * One-time mission-area setup step for unit admins landing in a workspace
 * with no shared mission AOIs yet: pick preset areas derived from the unit's
 * base locations, or skip (persisted per unit so it never nags again).
 */

import { useMemo, useState } from 'react';
import { Check, MapPinned } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAreasQuery, useCreateArea } from '@/queries/use-area-queries';
import { useLocationsQuery } from '@/queries/use-location-queries';
import { derivePresetAreas, sharedMissionAois, type AreaPreset } from './area-presets';
import vtakLogo from '@/assets/vtak-logo.png';

/** localStorage key prefix recording that a unit's admin skipped area setup */
const SKIP_KEY_PREFIX = 'vtak.areaSetup.skipped.';

/** True when an admin previously skipped area setup for this unit */
export function isAreaSetupSkipped(unitId: string): boolean {
  try {
    return window.localStorage.getItem(SKIP_KEY_PREFIX + unitId) === 'true';
  } catch {
    return false;
  }
}

/** Persist the skip so the setup step never nags this browser again */
function markAreaSetupSkipped(unitId: string): void {
  try {
    window.localStorage.setItem(SKIP_KEY_PREFIX + unitId, 'true');
  } catch {
    // storage unavailable — the dismissal still holds for this session
  }
}

/**
 * Renders the setup step only when the active unit has zero shared mission
 * AOIs and the admin hasn't skipped it. Mounted by the workspace gate for
 * admins; overlays the app until confirmed or skipped.
 */
export function MissionAreaSetupGate({ unitId }: { unitId: string }): React.JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);
  const areas = useAreasQuery(unitId);

  if (dismissed || isAreaSetupSkipped(unitId)) return null;
  if (!areas.isSuccess || sharedMissionAois(areas.data).length > 0) return null;
  return <MissionAreaSetup unitId={unitId} onDone={() => setDismissed(true)} />;
}

/** Full-screen first-run step: pick preset AOs from base locations, or skip. */
export function MissionAreaSetup({
  unitId,
  onDone,
}: {
  unitId: string;
  onDone: () => void;
}): React.JSX.Element {
  const locations = useLocationsQuery(unitId);
  const create = useCreateArea(unitId);
  const presets = useMemo(() => derivePresetAreas(locations.data ?? []), [locations.data]);
  // Presets default to selected; only deselections are tracked so the list
  // needs no state sync when the locations query resolves.
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selected = presets.filter((preset) => !deselected.has(preset.name));

  function toggle(name: string): void {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function confirm(): Promise<void> {
    setSaving(true);
    setError('');
    try {
      for (const preset of selected) {
        await create.mutateAsync({ name: preset.name, kind: 'area', geometry: preset.geometry });
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Saving areas failed');
    } finally {
      setSaving(false);
    }
  }

  function skip(): void {
    markAreaSetupSkipped(unitId);
    onDone();
  }

  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d] p-4">
      <div className="w-full max-w-md rounded-xl border border-echo-border bg-[#1a1a1a] p-6 shadow-2xl">
        <div className="mb-5 flex flex-col items-center gap-3">
          <img src={vtakLogo} alt="VTAK" className="h-11 w-auto" />
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Set up mission areas
          </p>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          This unit has no shared mission areas yet. Add preset areas around your base locations so
          members see the AO on the map right away.
        </p>

        {locations.isLoading ? (
          <p className="py-6 text-center text-xs text-muted-foreground animate-pulse">
            Loading base locations…
          </p>
        ) : presets.length > 0 ? (
          <ul className="mb-4 flex flex-col gap-1.5">
            {presets.map((preset) => (
              <li key={preset.name}>
                <PresetRow
                  preset={preset}
                  checked={!deselected.has(preset.name)}
                  onToggle={() => toggle(preset.name)}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-4 rounded-md border border-echo-border bg-white/[0.03] px-3 py-4 text-center text-xs text-muted-foreground">
            No base locations found for this unit, so there are no presets to offer.
          </p>
        )}

        <p className="mb-4 text-[11px] text-muted-foreground/80">
          Custom shapes can be drawn later via Tools → Drawing → Save as Mission Area.
        </p>

        {error && <p className="mb-3 text-xs text-red-400">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={skip}
            disabled={saving}
            className="rounded-md px-3 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            Skip for now
          </button>
          {presets.length > 0 && (
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={saving || selected.length === 0}
              className="rounded-md bg-echo-yellow px-4 py-2 text-xs font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Adding…' : `Add ${selected.length} area${selected.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A single checkbox row for a preset area. */
function PresetRow({
  preset,
  checked,
  onToggle,
}: {
  preset: AreaPreset;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors focus-visible:outline-none',
        checked
          ? 'border-echo-yellow/40 bg-echo-yellow/10'
          : 'border-echo-border bg-transparent hover:bg-white/[0.04]',
      )}
    >
      <span
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          checked ? 'border-echo-yellow bg-echo-yellow/20' : 'border-echo-border',
        )}
      >
        {checked && <Check className="h-3 w-3 text-echo-yellow" />}
      </span>
      <MapPinned className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{preset.name}</span>
    </button>
  );
}
